import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Input,
  Select,
  Row,
  Col,
  Button,
  Popover,
  Checkbox,
  DatePicker,
  TimePicker,
  Space,
  Divider,
  Typography,
  Modal,
  message,
} from "antd";
import {
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/es_ES";
import { TRANSFER_STATUSES } from "../../../constants/trasladosStatuses";
import debounce from "lodash/debounce";
import api from "../../../axiosConfig";
import { mensajeErrorDescarga } from "../../../utils/descargas";
import dayjs from "../../../utils/dayjs";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Filtro principal para Coordinación.
 *
 * Se agregan dos nuevos filtros: Departamento y Ciudad, que usan el ID como valor.
 * Al seleccionar un departamento, se carga la lista de ciudades asociadas desde la API
 * y se limpia el filtro de ciudad.
 */
const TrasladosFilter = ({
  onSearchTraslados,
  onVehicleFilter,
  onConfirmacionFilter,
  onValidacionFilter,
  onZonaFilter,
  onStatusFilter,
  onDateChange,
  onPrevDay,
  onNextDay,
  onPeriodChange,
  selectedPeriod,
  selectedDate,
  moviles,
  zonas,
  onManualFilterChange,
  onDepartamentoFilter, // Callback para actualizar el filtro de departamento (ID)
  onCiudadFilter, // Callback para actualizar el filtro de ciudad (ID)
  onCiudadesChange, // Callback para actualizar la lista de ciudades en el componente padre
  selectedCiudad, // <-- Agregado para controlar el Select de Ciudad
  onCopagoFilter, // Nueva prop para el filtro de copago
  onHourFilter,
  selectedHour,
  isLoading = false, // Estado de carga
  // Props para filtro de EPS
  onEpsFilter,
  selectedEps,
}) => {
  const [visiblePopover, setVisiblePopover] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCopagoValues, setSelectedCopagoValues] = useState([
    "con_copago",
    "sin_copago",
  ]); // Inicialmente ambos seleccionados
  const [localSearch, setLocalSearch] = useState("");
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportDateRange, setExportDateRange] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedVehicleCode, setSelectedVehicleCode] = useState(null); // Estado local para el código del vehículo seleccionado

  // Estados para filtro de EPS
  const [epsList, setEpsList] = useState([]);
  const [epsLoaded, setEpsLoaded] = useState(false);
  const [localEps, setLocalEps] = useState(selectedEps || "");

  // Cargar departamentos desde la API al montar
  useEffect(() => {
    const fetchDepartamentos = async () => {
      try {
        const response = await api.get("/departamentos");
        setDepartamentos(response.data);
      } catch (error) {
        console.error("Error al obtener departamentos:", error);
      }
    };
    fetchDepartamentos();
  }, []);

  // Función para cargar EPS (filtrado por rol del usuario)
  const fetchEps = async () => {
    try {
      // Usar endpoint que filtra por rol del usuario
      const response = await api.get("/roles/clientes/usuario");
      const { clientes, tiene_restriccion } = response.data;

      if (tiene_restriccion && clientes && clientes.length > 0) {
        // Si tiene restricción, mostrar solo los clientes permitidos
        setEpsList(clientes.map(c => ({
          id: c.id,
          nombre_cliente: c.nombre
        })));
      } else {
        // Si no tiene restricción, cargar todos los clientes
        const allClientes = await api.get("/clientes");
        setEpsList(allClientes.data);
      }
    } catch (error) {
      console.error("[FILTROS] Error al cargar EPS:", error);
      // Fallback: cargar todos los clientes
      try {
        const fallback = await api.get("/clientes");
        setEpsList(fallback.data);
      } catch (e) {
        console.error("[FILTROS] Error en fallback de EPS:", e);
      }
    }
  };

  // Al seleccionar un departamento, cargar las ciudades correspondientes y limpiar la ciudad seleccionada
  const handleDepartamentoChange = async (value) => {
    // Obtener el nombre del departamento seleccionado para filtros WebSocket
    const depSeleccionado = departamentos.find((d) => d.id === value);
    const nombreDepartamento = depSeleccionado ? depSeleccionado.nombre : null;

    onDepartamentoFilter(value, nombreDepartamento);
    // Al cambiar de departamento, se limpia el filtro de ciudad
    onCiudadFilter(null, null);
    setCiudades([]); // Limpiar ciudades locales

    // Llamar onManualFilterChange para refrescar la lista (el filtro se desactiva al limpiar ciudad)
    if (onManualFilterChange) onManualFilterChange();

    // Si se borra la selección, no se carga ninguna ciudad
    if (!value) return;

    try {
      const response = await api.get("/ciudades", {
        params: { departamento_id: value },
      });
      setCiudades(response.data);
      if (onCiudadesChange) onCiudadesChange(response.data);
    } catch (error) {
      console.error("Error al obtener ciudades:", error);
    }
  };

  const handleCiudadChange = (value) => {
    // Obtener el nombre de la ciudad seleccionada para filtros WebSocket
    const ciudadSeleccionada = ciudades.find((c) => c.id === value);
    const nombreCiudad = ciudadSeleccionada ? ciudadSeleccionada.nombre : null;

    onCiudadFilter(value, nombreCiudad);
    if (onManualFilterChange) onManualFilterChange();
  };

  // Debounce para onPrevDay y onNextDay
  const debouncedOnPrevDayRef = useRef();
  const debouncedOnNextDayRef = useRef();

  useEffect(() => {
    setSelectedStatuses(TRANSFER_STATUSES);
    onStatusFilter(TRANSFER_STATUSES);
  }, [onStatusFilter]);

  // Removido: búsqueda automática mientras se escribe
  // Ahora solo busca cuando se presiona Enter

  useEffect(() => {
    debouncedOnPrevDayRef.current = debounce(() => {
      onPrevDay();
      if (onManualFilterChange) onManualFilterChange();
    }, 500);
    debouncedOnNextDayRef.current = debounce(() => {
      onNextDay();
      if (onManualFilterChange) onManualFilterChange();
    }, 500);
    return () => {
      debouncedOnPrevDayRef.current.cancel();
      debouncedOnNextDayRef.current.cancel();
    };
  }, [onPrevDay, onNextDay, onManualFilterChange]);

  useEffect(() => {
    onCopagoFilter(selectedCopagoValues);
  }, [onCopagoFilter, selectedCopagoValues]);

  const confirmaciones = [
    { label: "Confirmado", value: "Confirmado" },
    { label: "Sin Confirmar", value: "Sin Confirmar" },
  ];

  const validaciones = [
    { label: "Validado", value: "Validado" },
    { label: "Sin Validar", value: "Sin Validar" },
  ];

  const handleStatusChange = (checkedValues) => {
    setSelectedStatuses(checkedValues);
    // No llamar onStatusFilter aquí - será manual
  };

  const aplicarFiltroEstados = () => {
    onStatusFilter(selectedStatuses);
    if (onManualFilterChange) onManualFilterChange();
    setVisiblePopover(false); // Cerrar el popover después de aplicar
  };

  const popoverContent = (
    <div>
      <Space direction="vertical">
        <Space>
          <Button
            size="small"
            onClick={() => {
              setSelectedStatuses(TRANSFER_STATUSES);
            }}
          >
            Seleccionar T
          </Button>
          <Button
            size="small"
            onClick={() => {
              setSelectedStatuses([]);
            }}
          >
            Deseleccionar T
          </Button>
        </Space>
        <Checkbox.Group
          options={TRANSFER_STATUSES.map((status) => ({
            label: status,
            value: status,
          }))}
          value={selectedStatuses}
          onChange={handleStatusChange}
          style={{ display: "flex", flexDirection: "column" }}
        />
        <Divider style={{ margin: "8px 0" }} />
        <Text strong>Filtro por Copago:</Text>
        <Checkbox.Group
          options={[
            { label: "Con Copago", value: "con_copago" },
            { label: "Sin Copago", value: "sin_copago" },
          ]}
          value={selectedCopagoValues}
          onChange={(values) => {
            setSelectedCopagoValues(values);
            onCopagoFilter(values);
            if (onManualFilterChange) onManualFilterChange();
          }}
          style={{ display: "flex", flexDirection: "column" }}
        />
        <Divider style={{ margin: "8px 0" }} />
        <Button
          type="primary"
          size="small"
          onClick={aplicarFiltroEstados}
          style={{ width: "100%" }}
        >
          Aplicar Filtro
        </Button>
      </Space>
    </div>
  );

  const handlePeriodChangeLocal = (value) => {
    onPeriodChange(value);
    if (onManualFilterChange) onManualFilterChange();
  };

  const handleVehicleFilter = (value) => {
    setSelectedVehicleCode(value);
    onVehicleFilter(value);
    if (onManualFilterChange) onManualFilterChange();
  };

  const handleConfirmacionFilter = (value) => {
    onConfirmacionFilter(value);
    if (onManualFilterChange) onManualFilterChange();
  };

  const handleValidacionFilter = (value) => {
    onValidacionFilter(value);
    if (onManualFilterChange) onManualFilterChange();
  };

  const handleZonaFilter = (value) => {
    onZonaFilter(value);
    if (onManualFilterChange) onManualFilterChange();
  };

  const handleDateChangeLocal = (date) => {
    onDateChange(date);
    if (onManualFilterChange) onManualFilterChange();
  };

  //handleExport
  const handleExport = async () => {
    if (!exportDateRange || !exportDateRange[0] || !exportDateRange[1]) {
      message.error("Por favor seleccione un rango de fechas");
      return;
    }

    const startDate = exportDateRange[0];
    const endDate = exportDateRange[1];
    const diffInDays = Math.abs(endDate.diff(startDate, "days"));

    if (diffInDays > 90) {
      message.error("El rango de fechas no puede ser mayor a 3 meses");
      return;
    }

    try {
      setExportLoading(true);
      const response = await api.get("/traslados/exportar", {
        params: {
          fechaInicio: startDate.format("YYYY-MM-DD"),
          fechaFin: endDate.format("YYYY-MM-DD"),
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `traslados_${startDate.format("YYYY-MM-DD")}_${endDate.format(
          "YYYY-MM-DD"
        )}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Reporte exportado exitosamente");
      setExportModalVisible(false);
      setExportDateRange(null);
    } catch (error) {
      console.error("Error al exportar el reporte:", error);
      message.error(await mensajeErrorDescarga(error, "Error al exportar el reporte"));
    } finally {
      setExportLoading(false);
    }
  };

  /**
   * Convierte un string numérico en formato de hora válido
   * @param {string} value - El valor a convertir
   * @returns {string|null} - La hora en formato HH:mm o null si no es válido
   */
  const convertToTimeFormat = (value) => {
    // Si no hay valor, retornamos null
    if (!value) return null;

    // Removemos cualquier caracter que no sea número
    const numbers = value.replace(/[^0-9]/g, "");

    // Si no hay números, retornamos null
    if (numbers.length === 0) return null;

    // Manejamos diferentes casos según la cantidad de números
    switch (numbers.length) {
      case 1: // Un solo dígito (ej: "5" -> "05:00")
        return `0${numbers}:00`;
      case 2: // Dos dígitos (ej: "17" -> "17:00")
        const hours = parseInt(numbers);
        return hours >= 0 && hours <= 23
          ? `${numbers.padStart(2, "0")}:00`
          : null;
      case 3: // Tres dígitos (ej: "530" -> "05:30")
        const h = numbers.substring(0, 1);
        const m = numbers.substring(1);
        return parseInt(h) >= 0 &&
          parseInt(h) <= 23 &&
          parseInt(m) >= 0 &&
          parseInt(m) <= 59
          ? `0${h}:${m}`
          : null;
      case 4: // Cuatro dígitos (ej: "1730" -> "17:30")
        const hh = numbers.substring(0, 2);
        const mm = numbers.substring(2);
        return parseInt(hh) >= 0 &&
          parseInt(hh) <= 23 &&
          parseInt(mm) >= 0 &&
          parseInt(mm) <= 59
          ? `${hh}:${mm}`
          : null;
      default:
        return null;
    }
  };

  return (
    <div style={{ marginBottom: 16, width: "100%" }}>
      <Row wrap gutter={[16, 16]} style={{ alignItems: "center" }}>
        {/* Búsqueda de Traslados*/}
        <Col xs={24} sm={12} md={4}>
          <Search
            placeholder="(Presione Enter)"
            size="large"
            onSearch={(value) => {
              onSearchTraslados(value);
              if (onManualFilterChange) onManualFilterChange();
            }}
            onChange={(e) => setLocalSearch(e.target.value)}
            onPressEnter={(e) => {
              onSearchTraslados(e.target.value);
              if (onManualFilterChange) onManualFilterChange();
            }}
            value={localSearch}
            allowClear
            onClear={() => {
              setLocalSearch("");
              onSearchTraslados("");
              if (onManualFilterChange) onManualFilterChange();
            }}
            disabled={isLoading}
            enterButton="Buscar"
          />
        </Col>

        {/* Filtro por Período */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Período"
            size="large"
            onChange={handlePeriodChangeLocal}
            value={selectedPeriod}
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            <Option value="daily">Diario</Option>
            <Option value="currentWeek">Sem. Actual</Option>
            <Option value="previousWeek">Sem. Anterior</Option>
            <Option value="currentMonth">Mes Actual</Option>
            <Option value="previousMonth">Mes Anterior</Option>
          </Select>
        </Col>

        {/* Filtro de Hora Exacta */}
        {selectedPeriod === "daily" && (
          <Col xs={24} sm={12} md={2}>
            <TimePicker
              size="large"
              placeholder="Hora Exacta"
              value={selectedHour ? dayjs(selectedHour) : null}
              onChange={(time) => {
                onHourFilter(time ? time.toDate() : null);
                if (onManualFilterChange) onManualFilterChange();
              }}
              format="HH:mm"
              style={{ width: "100%" }}
              allowClear
              inputReadOnly={false}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const timeStr = convertToTimeFormat(e.target.value);
                  if (timeStr) {
                    const [hours, minutes] = timeStr.split(":");
                    const newDate = dayjs().hour(parseInt(hours)).minute(parseInt(minutes)).second(0).toDate();
                    onHourFilter(newDate);
                    if (onManualFilterChange) onManualFilterChange();
                  }
                }
              }}
            />
          </Col>
        )}

        {/* Filtro por Vehículo */}
        <Col xs={24} sm={24} md={2}>
          <Select
            placeholder="Vehículo"
            allowClear
            size="large"
            value={selectedVehicleCode}
            onChange={handleVehicleFilter}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children?.toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            {moviles.map((movil) => (
              <Option key={movil.codigo} value={movil.codigo}>
                {`${movil.codigo} - ${movil.placa}`}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Filtro por Confirmación */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Confirmación"
            allowClear
            size="large"
            onChange={handleConfirmacionFilter}
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            {confirmaciones.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Filtro por Validación */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Validación"
            allowClear
            size="large"
            onChange={handleValidacionFilter}
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            {validaciones.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Filtro por Departamento */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Departamento"
            allowClear
            size="large"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleDepartamentoChange}
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            {departamentos.map((dep) => (
              <Option key={dep.id} value={dep.id}>
                {dep.nombre}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Filtro por Ciudad */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Ciudad"
            allowClear
            size="large"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            value={selectedCiudad} // Componente controlado
            onChange={handleCiudadChange}
            style={{ width: "100%" }}
            disabled={isLoading || !ciudades || ciudades.length === 0}
          >
            {ciudades.map((city) => (
              <Option key={city.id} value={city.id}>
                {city.nombre}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Filtro por EPS */}
        <Col xs={24} sm={12} md={2}>
          <Select
            showSearch
            placeholder="EPS"
            value={localEps || undefined}
            onChange={(value) => {
              setLocalEps(value || "");
              if (onEpsFilter) {
                onEpsFilter(value || "");
              }
              if (onManualFilterChange) onManualFilterChange();
            }}
            onFocus={() => {
              if (!epsLoaded) {
                fetchEps();
                setEpsLoaded(true);
              }
            }}
            filterOption={(input, option) =>
              option.children?.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            size="large"
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            <Option value="">Todas las EPS</Option>
            {epsList.map((eps) => (
              <Option key={eps.id} value={eps.nombre_cliente}>
                {eps.nombre_cliente}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Ícono para Filtro por Estados */}
        <Col xs={24} sm={24} md={2}>
          <Popover
            content={popoverContent}
            title="Estados"
            trigger="click"
            open={visiblePopover}
            onOpenChange={(v) => setVisiblePopover(v)}
          >
            <Button
              icon={<FilterOutlined />}
              size="large"
              style={{ width: "100%" }}
              aria-label="Filtros de Estados"
              disabled={isLoading}
            />
          </Popover>
        </Col>
        {/* Filtro por Zona */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Zona"
            allowClear
            size="large"
            onChange={handleZonaFilter}
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            {zonas.map((zona) => (
              <Option key={zona.id} value={zona.nombre}>
                {zona.nombre}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Navegación de fecha - fila separada centrada */}
      {selectedPeriod === "daily" && (
        <Row
          wrap
          gutter={[16, 16]}
          justify="center"
          style={{ marginTop: 16, position: "relative" }}
        >
          <Col>
            <Space size="middle">
              <Button
                icon={<LeftOutlined />}
                onClick={() => debouncedOnPrevDayRef.current()}
                size="large"
                disabled={isLoading}
              />
              <DatePicker
                value={selectedDate}
                onChange={handleDateChangeLocal}
                format="DD/MM/YYYY"
                locale={locale}
                allowClear={false}
                size="large"
                disabled={isLoading}
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => debouncedOnNextDayRef.current()}
                size="large"
                disabled={isLoading}
              />
            </Space>
          </Col>

          {/* Botón de exportación a la derecha */}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            onClick={() => setExportModalVisible(true)}
            style={{
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
              position: "absolute",
              right: 0,
            }}
            disabled={isLoading}
          />
        </Row>
      )}

      {/* Modal de Exportación */}
      <Modal
        title="Exportar Traslados"
        open={exportModalVisible}
        onCancel={() => {
          setExportModalVisible(false);
          setExportDateRange(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setExportModalVisible(false);
              setExportDateRange(null);
            }}
          >
            Cancelar
          </Button>,
          <Button
            key="export"
            type="primary"
            loading={exportLoading}
            onClick={handleExport}
            icon={<DownloadOutlined />}
            title="Exportar Traslados"
          >
            Exportar
          </Button>,
        ]}
      >
        <div style={{ padding: "20px 0" }}>
          <h4>Seleccione el rango de fechas:</h4>
          <p style={{ color: "rgba(0, 0, 0, 0.45)", marginBottom: "16px" }}>
            El rango máximo permitido es de 3 meses
          </p>
          <RangePicker
            style={{ width: "100%" }}
            onChange={(dates) => setExportDateRange(dates)}
            format="DD/MM/YYYY"
            disabled={isLoading}
          />
        </div>
      </Modal>
    </div>
  );
};

TrasladosFilter.propTypes = {
  onSearchTraslados: PropTypes.func.isRequired,
  onVehicleFilter: PropTypes.func.isRequired,
  onConfirmacionFilter: PropTypes.func.isRequired,
  onValidacionFilter: PropTypes.func.isRequired,
  onZonaFilter: PropTypes.func.isRequired,
  onStatusFilter: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onPrevDay: PropTypes.func.isRequired,
  onNextDay: PropTypes.func.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  selectedPeriod: PropTypes.string.isRequired,
  selectedDate: PropTypes.object,
  moviles: PropTypes.array.isRequired,
  zonas: PropTypes.array.isRequired,
  onManualFilterChange: PropTypes.func,
  onDepartamentoFilter: PropTypes.func,
  onCiudadFilter: PropTypes.func,
  onCiudadesChange: PropTypes.func,
  onCopagoFilter: PropTypes.func.isRequired,
  onHourFilter: PropTypes.func,
  selectedHour: PropTypes.object,
  isLoading: PropTypes.bool,
  onEpsFilter: PropTypes.func,
  selectedEps: PropTypes.string,
};

export default TrasladosFilter;
